import { getCookie } from 'cookies-next';

import { endpoints, http, logger } from '@/config';
import httpFetch from '@/config/httpFetch';
import {
  DEFAULT_STOCK_ID,
  DEFAULT_STORE_CODE,
  MAGENTO_GRAPHQL_CAROUSEL_TYPES,
  REVALIDATE_TIME,
} from '@/constants';
import { BannerImageTypes } from '@/models/global';

export const getBannerImages = async (
  storeCode?: string,
  userId?: string | null
) => {
  try {
    const options: any = {
      next: { revalidate: REVALIDATE_TIME },
      store_code: storeCode,
    };
    if (userId) {
      options.headers = {
        'user-id': userId,
      };
    }
    const { data } = await http.get(`${endpoints.BANNER_IMAGES}`, options);
    return data;
  } catch (error) {
    logger.log('Error', error);
  }
};

export const getFaqData = async (urlKey: string) => {
  const { data } = await httpFetch.get(
    `${process.env.NEXT_PUBLIC_CDN_LINK}/faqs/${urlKey}.json`
  );
  return data;
};

export const getMagentoCarouselsGraphQl = async ({
  store_code = DEFAULT_STORE_CODE,
  token,
  carouselType = MAGENTO_GRAPHQL_CAROUSEL_TYPES.HOMEPAGE,
  useNextCache = false,
  carouselCode,
  userId,
}: {
  store_code?: string;
  token?: string;
  carouselType?:
    | 'home_page'
    | 'trending_products'
    | 'cart_recommendations'
    | 'gifting'
    | 'additional';
  useNextCache?: boolean;
  carouselCode?: string;
  userId?: string | undefined;
}) => {
  const request = {
    query: `
    query CarouselsConfiguration {
        carouselsConfiguration(carouselType: "${carouselType}"${
          carouselCode ? `,carouselCode: "${carouselCode}"` : ''
        }) {
            carousel_no
            category_url_key
        }
    }
    `,
  };
  const options: any = {};
  if (store_code) {
    options.headers = {
      store: store_code,
    };
  }
  if (token) {
    options.token = token;
  }
  if (useNextCache) {
    options.next = { revalidate: REVALIDATE_TIME };
  }
  if (!userId) {
    const cookieUserId = getCookie('user-id');
    if (cookieUserId) {
      options.headers = {
        ...options.headers,
        'user-id': cookieUserId,
      };
    }
  } else {
    options.headers = {
      ...options.headers,
      'user-id': userId,
    };
  }
  const { data } = await http.post(
    endpoints.GRAPHQL_URI,
    request,
    Object.keys(options).length > 0 ? options : null
  );
  return data?.data?.carouselsConfiguration;
};

export const getProductStockWithStockId = async ({
  sku,
  stockId = DEFAULT_STOCK_ID,
  revalidate = REVALIDATE_TIME,
}: {
  sku: string;
  stockId: string | number;
  revalidate?: number;
}): Promise<boolean> => {
  const url = `inventory/is-product-salable/${sku}/${stockId}`;
  try {
    const { data } = await http.get(url, {
      next: { revalidate },
    });
    return data;
  } catch (e: unknown) {
    logger.error('Error while fetching stock status: ', e);
    return false;
  }
};

interface StockStatus {
  salable: boolean;
  sku: string;
  stock_id: number;
}

export const getProductsStockByStockId = async ({
  skus,
  stockId = DEFAULT_STOCK_ID,
  revalidate = REVALIDATE_TIME,
}: {
  skus: string[];
  stockId: string | number;
  revalidate?: number;
}): Promise<StockStatus[]> => {
  const skuParams = skus.map((sku, index) => `skus[${index}]=${sku}`).join('&');
  const url = `inventory/are-products-salable/?${skuParams}&stockId=${stockId}`;
  try {
    const { data } = await http.get(url, {
      next: { revalidate },
    });
    return data;
  } catch (e: unknown) {
    logger.error('Error while fetching stock status: ', e);
    return [];
  }
};

export const getFoodstoriesMenuBanner = async ({
  bannerCode,
  useCache = true,
  userId,
  storeCode = DEFAULT_STORE_CODE,
}: {
  bannerCode: string;
  useCache?: boolean;
  userId?: string | undefined;
  storeCode?: string;
}): Promise<BannerImageTypes[] | null> => {
  try {
    const options: any = {
      next: { revalidate: useCache ? REVALIDATE_TIME : 0 },
      store_code: storeCode,
    };
    if (!userId) {
      const cookieUserId = getCookie('user-id');
      if (cookieUserId) {
        options.headers = {
          ...options.headers,
          'user-id': cookieUserId,
        };
      }
    } else {
      options.headers = {
        ...options.headers,
        'user-id': userId,
      };
    }

    const { data } = await http.get(`banner/data/${bannerCode}`, options);
    const activeBanners = data?.filter((banner: any) => banner.status === '1');
    return activeBanners;
  } catch (error) {
    logger.log('Error while fetching menu banner', error);
    return null;
  }
};

export const getABTestingUserGroup = async () => {
  const userId = await getCookie('user-id');
  if (!userId) {
    return null;
  }
  const options = {
    headers: {
      'user-id': userId,
    },
    doNotIncludeRegion: true,
  };
  const { data } = await http.get('ab-test/list', options);
  return data;
};

type GetProductsUrlKeyProps = {
  categoryId?: number;
  storeCode?: string;
  useNextCache?: boolean;
};

export const getProductsUrlKey = async ({
  categoryId = 3,
  storeCode = DEFAULT_STORE_CODE,
  useNextCache = true,
}: GetProductsUrlKeyProps): Promise<Set<string> | null> => {
  const request = {
    query: `
    query Products {
    products(filter: { category_id: { in: ["${categoryId}"] } }, pageSize: 5000, currentPage: 1) {
        total_count
        items {
            url_key
        }
        page_info {
            current_page
            page_size
            total_pages
        }
    }
}`,
  };
  const options: any = {
    headers: {
      Store: storeCode,
    },
  };
  if (useNextCache) {
    options.next = { revalidate: REVALIDATE_TIME };
  }
  try {
    const { data } = await http.post(
      endpoints.GRAPHQL_URI,
      request,
      Object.keys(options).length > 0 ? options : null
    );
    const items = data?.data?.products?.items;
    return new Set(items?.map((item: { url_key: string }) => item.url_key));
  } catch (error) {
    logger.error('Error while fetching products url key:', error);
    return null;
  }
};

export const getFormFields = async (code: string) => {
  try {
    const { data } = await http.get(`${endpoints.FORM_FIELDS}/${code}`);
    return data;
  } catch (error) {
    logger.log('Error', error);
  }
};

export const submitForm = async ({
  form_code,
  fields,
}: {
  form_code: string;
  fields: object;
}) => {
  try {
    const { data: response } = await http.post(
      `${endpoints.FORM_FIELDS}/submit`,
      {
        form_code,
        fields,
      }
    );
    return response;
  } catch (error) {
    logger.log('Error', error);
  }
};

export const getTrustedByBrandsLogo = async () => {
  try {
    const { data } = await http.get(`${endpoints.TRUSTED_BY_BRANDS}`);
    return data;
  } catch (error) {
    logger.log('Error', error);
  }
};

export const applyCouponCode = async ({
  couponCode,
  cartId,
  isLoggedIn,
}: {
  couponCode: string;
  cartId?: string;
  isLoggedIn: boolean;
}) => {
  try {
    const endpoint = isLoggedIn
      ? `${endpoints.CUSTOMER_CART}/coupons/${couponCode}`
      : `${endpoints.GUEST_CART}/${cartId}/coupons/${couponCode}`;

    const { response, data } = await http.put(endpoint);
    return { success: response?.ok, data, response };
  } catch (error: any) {
    logger.log('Error applying coupon code', error);
    return {
      success: false,
      data: error?.response?.data || error?.data,
      response: error?.response,
    };
  }
};

export const removeCouponCode = async ({
  cartId,
  isLoggedIn,
}: {
  cartId?: string;
  isLoggedIn: boolean;
}) => {
  try {
    const endpoint = isLoggedIn
      ? `${endpoints.CUSTOMER_CART}/coupons`
      : `${endpoints.GUEST_CART}/${cartId}/coupons`;

    const { response, data } = await http.delete(endpoint);
    return { success: response?.ok, data, response };
  } catch (error: any) {
    logger.log('Error removing coupon code', error);
    return {
      success: false,
      data: error?.response?.data || error?.data,
      response: error?.response,
    };
  }
};
